﻿/**
 * Frontend Notification Service
 * Handles fetching, managing, and real-time updates for user notifications
 */

import apiClient from "../utils/apiClient";
import { supabase } from "../../lib/supabase";
import logger from "../utils/logger";

class NotificationService {
  constructor() {
    this.baseUrl = "/api/notifications";
    this.listeners = new Set();
    this.subscription = null;
  }

  /**
   * Get user's notifications
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Notifications data
   */
  async getNotifications(options = {}) {
    try {
      const params = {
        limit: options.limit || 50,
        offset: options.offset || 0,
        includeRead: options.includeRead !== false,
        category: options.category || null,
        priority: options.priority || null,
      };

      const response = await apiClient.get(this.baseUrl, { params });
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch notifications:", error);
      throw error;
    }
  }

  /**
   * Get unread notification count
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/unread-count`);
      return response.data.count || 0;
    } catch (error) {
      logger.error("Failed to fetch unread count:", error);
      return 0;
    }
  }

  /**
   * Get recent unread notifications (for header dropdown)
   * @param {number} limit - Number of notifications to fetch
   * @returns {Promise<Array>} Recent notifications
   */
  async getRecentNotifications(limit = 10) {
    try {
      const response = await apiClient.get(`${this.baseUrl}/recent`, {
        params: { limit },
      });
      return response.data.notifications || [];
    } catch (error) {
      logger.error("Failed to fetch recent notifications:", error);
      return [];
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @param {string} actionTaken - Optional action taken
   * @returns {Promise<Object>} Updated notification
   */
  async markAsRead(notificationId, actionTaken = null) {
    try {
      const response = await apiClient.post(
        `${this.baseUrl}/${notificationId}/read`,
        {
          actionTaken,
        },
      );

      // Notify listeners of the change
      this.notifyListeners("notification_read", {
        notificationId,
        actionTaken,
      });

      return response.data.notification;
    } catch (error) {
      logger.error("Failed to mark notification as read:", error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<void>}
   */
  async markAllAsRead() {
    try {
      await apiClient.post(`${this.baseUrl}/mark-all-read`);

      // Notify listeners of the change
      this.notifyListeners("all_notifications_read", {});
    } catch (error) {
      logger.error("Failed to mark all notifications as read:", error);
      throw error;
    }
  }

  /**
   * Get user notification preferences
   * @returns {Promise<Array>} User preferences
   */
  async getPreferences() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/preferences`);
      return response.data.preferences || [];
    } catch (error) {
      logger.error("Failed to fetch notification preferences:", error);
      return [];
    }
  }

  /**
   * Update notification preference
   * @param {string} typeId - Notification type ID
   * @param {boolean} enabled - Whether notifications are enabled
   * @returns {Promise<Object>} Updated preference
   */
  async updatePreference(typeId, enabled) {
    try {
      const response = await apiClient.put(
        `${this.baseUrl}/preferences/${typeId}`,
        {
          enabled,
        },
      );
      return response.data.preference;
    } catch (error) {
      logger.error("Failed to update notification preference:", error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time notification updates
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function for new notifications
   */
  subscribeToNotifications(userId, callback) {
    if (this.subscription) {
      this.unsubscribeFromNotifications();
    }

    this.subscription = supabase
      .channel(`notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `player_id=eq.${userId}`,
        },
        async (payload) => {
          // Fetch complete notification data with joins since real-time doesn't include them
          try {
            const { data, error } = await supabase
              .from("notifications")
              .select(
                `
              *,
              notification_types(
                image_url,
                display_name,
                category
              )
            `,
              )
              .eq("id", payload.new.id)
              .single();

            if (error) throw error;
            callback("new_notification", data);
          } catch (error) {
            logger.error("Failed to fetch complete notification data:", error);
            // Fallback to basic data if fetch fails
            callback("new_notification", payload.new);
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `player_id=eq.${userId}`,
        },
        async (payload) => {
          // Fetch complete notification data with joins since real-time doesn't include them
          try {
            const { data, error } = await supabase
              .from("notifications")
              .select(
                `
              *,
              notification_types(
                image_url,
                display_name,
                category
              )
            `,
              )
              .eq("id", payload.new.id)
              .single();

            if (error) throw error;
            callback("notification_updated", data);
          } catch (error) {
            logger.error("Failed to fetch complete notification data:", error);
            // Fallback to basic data if fetch fails
            callback("notification_updated", payload.new);
          }
        },
      )
      .subscribe();
  }

  /**
   * Unsubscribe from real-time notifications
   */
  unsubscribeFromNotifications() {
    if (this.subscription) {
      supabase.removeChannel(this.subscription);
      this.subscription = null;
    }
  }

  /**
   * Add listener for notification events
   * @param {Function} listener - Listener function
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove listener
   * @param {Function} listener - Listener function
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of an event
   * @param {string} event - Event type
   * @param {Object} data - Event data
   */
  notifyListeners(event, data) {
    this.listeners.forEach((listener) => {
      try {
        listener(event, data);
      } catch (error) {
        logger.error("Error in notification listener:", error);
      }
    });
  }

  /**
   * Format notification for display
   * @param {Object} notification - Raw notification object
   * @returns {Object} Formatted notification
   */
  formatNotification(notification) {
    // Debug: Log the raw notification to see what we're getting
    if (!notification.created_at) {
      logger.warn("Notification missing created_at field:", notification);
    }

    // Get image URL from notification type if available
    const imageUrl = notification.notification_types?.image_url;
    const icon = notification.icon || "ðŸ””";

    return {
      id: notification.id,
      title: notification.title,
      message: notification.message,
      icon: icon,
      imageUrl: imageUrl, // Add image URL from notification type
      actionUrl: notification.action_url,
      priority: notification.priority || "normal",
      isRead: notification.is_read,
      createdAt: notification.created_at
        ? new Date(notification.created_at)
        : new Date(),
      metadata: notification.metadata || {},
    };
  }

  /**
   * Get time ago string for notification
   * @param {Date} date - Notification date
   * @returns {string} Time ago string
   */
  getTimeAgo(date) {
    try {
      // Ensure we have a valid date
      const notificationDate = date instanceof Date ? date : new Date(date);

      // Check if date is valid
      if (isNaN(notificationDate.getTime())) {
        logger.warn("Invalid date provided to getTimeAgo:", date);
        return "Recently";
      }

      const now = new Date();
      const diffInSeconds = Math.floor((now - notificationDate) / 1000);

      // Handle negative differences (future dates)
      if (diffInSeconds < 0) {
        return "Just now";
      }

      if (diffInSeconds < 60) {
        return "Just now";
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes}m ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours}h ago`;
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days}d ago`;
      }
    } catch (error) {
      logger.error("Error in getTimeAgo:", error);
      return "Recently";
    }
  }

  /**
   * Get priority color for notification
   * @param {string} priority - Notification priority
   * @returns {string} CSS color class
   */
  getPriorityColor(priority) {
    switch (priority) {
      case "high":
        return "text-red-400";
      case "medium":
        return "text-primary";
      case "low":
        return "text-gray-400";
      default:
        return "text-blue-400";
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
